import React, { useEffect, useMemo, useState } from 'react';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import { useDocTitle } from '../components/CustomHook';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db, storage } from '../firebase';
import {
    collection, onSnapshot, orderBy, query,
    serverTimestamp, addDoc,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import heic2any from 'heic2any';

/* ─── helpers ────────────────────────────────────────────────────────────── */

const toDisplay = (tag) =>
    tag ? tag.charAt(0).toUpperCase() + tag.slice(1) : '';

const toKey = (tag) => tag.trim().toLowerCase();

/* ─── component ──────────────────────────────────────────────────────────── */

const PhotoLibrary = () => {
    useDocTitle('photo library');

    const [user] = useAuthState(auth);

    // data
    const [photos, setPhotos] = useState([]);
    const [tags, setTags] = useState([]);   // array of lowercase tag strings

    // gallery state
    const [selectedTag, setSelectedTag] = useState('All');
    const [selectedIndex, setSelectedIndex] = useState(null);

    // upload modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [photoName, setPhotoName] = useState('');
    const [chosenTag, setChosenTag] = useState('');    // selected from dropdown
    const [newTagValue, setNewTagValue] = useState('');
    const [addingNewTag, setAddingNewTag] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadMsg, setUploadMsg] = useState('');

    /* ── firestore listeners ─────────────────────────────────────────────── */

    useEffect(() => {
        const q = query(collection(db, 'photos'), orderBy('createdAt', 'desc'));
        return onSnapshot(q, (snap) => {
            setPhotos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });
    }, []);

    useEffect(() => {
        const q = query(collection(db, 'tags'), orderBy('name'));
        return onSnapshot(q, (snap) => {
            const loaded = snap.docs.map((d) => d.data().name);
            setTags(loaded);
            // seed dropdown selection when tags first arrive
            setChosenTag((prev) => (prev === '' && loaded.length ? loaded[0] : prev));
        });
    }, []);

    /* ── derived data ────────────────────────────────────────────────────── */

    const filteredPhotos = useMemo(() => {
        if (selectedTag === 'All') return photos;
        return photos.filter((p) => toKey(p.tag) === toKey(selectedTag));
    }, [photos, selectedTag]);

    const tagCounts = useMemo(() => {
        const counts = { All: photos.length };
        tags.forEach((t) => {
            counts[t] = photos.filter((p) => toKey(p.tag) === toKey(t)).length;
        });
        return counts;
    }, [photos, tags]);

    /* ── keyboard nav ────────────────────────────────────────────────────── */

    useEffect(() => {
        if (selectedIndex === null) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setSelectedIndex(null);
            if (e.key === 'ArrowLeft') setSelectedIndex((i) => (i > 0 ? i - 1 : i));
            if (e.key === 'ArrowRight')
                setSelectedIndex((i) => (i < filteredPhotos.length - 1 ? i + 1 : i));
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedIndex, filteredPhotos.length]);

    /* ── upload handlers ─────────────────────────────────────────────────── */

    const handleFileChange = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;

        if (!photoName) {
            setPhotoName(f.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '));
        }

        const isHeic = /\.(heic|heif)$/i.test(f.name) || f.type === 'image/heic' || f.type === 'image/heif';

        if (isHeic) {
            setUploadMsg('Converting HEIC…');
            try {
                // heic2any decodes HEIC in the browser, returns a JPEG Blob
                const jpegBlob = await heic2any({ blob: f, toType: 'image/jpeg', quality: 0.92 });
                const converted = new File([jpegBlob], f.name.replace(/\.[^/.]+$/i, '.jpg'), { type: 'image/jpeg' });
                setFile(converted);
                const reader = new FileReader();
                reader.onload = (ev) => setPreview(ev.target.result);
                reader.readAsDataURL(converted);
                setUploadMsg('');
            } catch (err) {
                console.error('HEIC conversion failed:', err);
                setUploadMsg('Could not convert HEIC file. Try saving it as a JPG first.');
            }
        } else {
            setFile(f);
            const reader = new FileReader();
            reader.onload = (ev) => setPreview(ev.target.result);
            reader.readAsDataURL(f);
        }
    };

    const handleUpload = async () => {
        const finalTag = addingNewTag ? newTagValue.trim() : chosenTag;
        if (!file || !photoName.trim() || !finalTag) return;

        setUploading(true);
        setUploadMsg('');

        try {
            // Compress + convert to JPEG
            const compressed = await imageCompression(file, {
                maxSizeMB: 0.8,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                fileType: 'image/jpeg',
                initialQuality: 0.82,
            });

            const slug = `${Date.now()}-${photoName.trim().replace(/\s+/g, '-').toLowerCase()}.jpg`;
            const storageRef = ref(storage, `photo-library/${slug}`);
            await uploadBytes(storageRef, compressed);
            const imageUrl = await getDownloadURL(storageRef);

            const tagKey = toKey(finalTag);

            await addDoc(collection(db, 'photos'), {
                name: photoName.trim(),
                tag: tagKey,
                imageUrl,
                createdAt: serverTimestamp(),
            });

            // Persist new tag if it doesn't already exist
            if (addingNewTag && tagKey && !tags.includes(tagKey)) {
                await addDoc(collection(db, 'tags'), {
                    name: tagKey,
                    createdAt: serverTimestamp(),
                });
            }

            setUploadMsg('Upload complete!');
            setTimeout(resetModal, 1600);
        } catch (err) {
            console.error('Upload error:', err);
            const msg = err?.message || err?.code || String(err);
            setUploadMsg(`Upload failed: ${msg}`);
            setUploading(false);
        }
    };

    const resetModal = () => {
        setModalOpen(false);
        setFile(null);
        setPreview(null);
        setPhotoName('');
        setChosenTag(tags[0] ?? '');
        setNewTagValue('');
        setAddingNewTag(false);
        setUploading(false);
        setUploadMsg('');
    };

    const selectedPhoto = selectedIndex !== null ? filteredPhotos[selectedIndex] : null;

    /* ── render ──────────────────────────────────────────────────────────── */

    return (
        <>
            <NavBar />

            {/* ── Hero ─────────────────────────────────────────────────── */}
            <div
                className="hero bg-gray-900 text-white pt-32 pb-20 px-6 text-center"
                style={{ fontFamily: "'Syne', sans-serif" }}
            >
                <div className="max-w-2xl mx-auto">
                    <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm text-gray-300 mb-6">
                        Community Moments
                    </div>
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold mb-5 leading-tight">
                        Photo Library
                    </h1>
                    <p className="text-gray-400 text-lg leading-relaxed">
                        A visual journey through our events, community,<br className="hidden sm:block" />
                        and the people that make Saving Strides what it is.
                    </p>
                </div>
            </div>

            {/* ── Tag filter bar ───────────────────────────────────────── */}
            <div className="bg-white border-b border-gray-100 shadow-sm">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="flex items-center gap-2 overflow-x-auto py-4 no-scrollbar">
                        {['All', ...tags].map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => { setSelectedTag(t); setSelectedIndex(null); }}
                                className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                                    selectedTag === t
                                        ? 'bg-gray-900 text-white shadow-sm'
                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-800'
                                }`}
                            >
                                {t === 'All' ? 'All' : toDisplay(t)}
                                <span className="text-xs tabular-nums text-gray-400">
                                    {tagCounts[t] ?? 0}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Gallery ──────────────────────────────────────────────── */}
            <div className="bg-gray-50 min-h-screen py-10 px-4 sm:px-6">
                <div className="max-w-6xl mx-auto">

                    <p className="text-xs text-gray-400 font-medium mb-6 tracking-wide uppercase">
                        {filteredPhotos.length} {filteredPhotos.length === 1 ? 'photo' : 'photos'}
                        {selectedTag !== 'All' && ` · ${toDisplay(selectedTag)}`}
                    </p>

                    {filteredPhotos.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-32 text-center">
                            <div className="w-16 h-16 rounded-2xl bg-white border border-gray-200 flex items-center justify-center mb-4 shadow-sm">
                                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <p className="text-gray-500 font-medium text-sm">No photos yet.</p>
                        </div>
                    ) : (
                        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
                            {filteredPhotos.map((photo, index) => (
                                <div
                                    key={photo.id}
                                    className="break-inside-avoid mb-4 group relative cursor-pointer rounded-2xl overflow-hidden bg-gray-200 shadow-sm hover:shadow-xl transition-shadow duration-300"
                                    onClick={() => setSelectedIndex(index)}
                                >
                                    <img
                                        src={photo.imageUrl}
                                        alt={photo.name || 'Community photo'}
                                        className="w-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                                        {photo.tag && (
                                            <span className="inline-block self-start bg-white/15 backdrop-blur-sm border border-white/25 text-white text-xs font-medium rounded-full px-2.5 py-0.5 mb-2 tracking-wide">
                                                {toDisplay(photo.tag)}
                                            </span>
                                        )}
                                        <p className="text-white font-semibold text-sm leading-snug">
                                            {photo.name || 'Community photo'}
                                        </p>
                                    </div>
                                    <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                        </svg>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ── Admin upload button (bottom of gallery) ───────── */}
                    {user && (
                        <div className="mt-14 flex justify-center">
                            <button
                                type="button"
                                onClick={() => setModalOpen(true)}
                                className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors shadow-md"
                            >
                                <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 5a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H6a1 1 0 010-2h5V6a1 1 0 011-1z" />
                                </svg>
                                Upload Photo
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Lightbox ─────────────────────────────────────────────── */}
            {selectedPhoto && (
                <div
                    className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center px-4 py-6"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => setSelectedIndex(null)}
                >
                    <button
                        className={`absolute left-3 sm:left-5 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all duration-150 ${
                            selectedIndex === 0 ? 'opacity-20 pointer-events-none' : 'opacity-100'
                        }`}
                        onClick={(e) => { e.stopPropagation(); setSelectedIndex((i) => i - 1); }}
                        aria-label="Previous photo"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <button
                        className={`absolute right-3 sm:right-5 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all duration-150 ${
                            selectedIndex === filteredPhotos.length - 1 ? 'opacity-20 pointer-events-none' : 'opacity-100'
                        }`}
                        onClick={(e) => { e.stopPropagation(); setSelectedIndex((i) => i + 1); }}
                        aria-label="Next photo"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                    <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-3 px-1">
                            <div className="flex items-center gap-3">
                                {selectedPhoto.tag && (
                                    <span className="text-xs font-medium uppercase tracking-widest text-gray-500">
                                        {toDisplay(selectedPhoto.tag)}
                                    </span>
                                )}
                                <span className="text-white font-medium text-sm">
                                    {selectedPhoto.name || 'Community photo'}
                                </span>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="text-gray-500 text-xs tabular-nums">
                                    {selectedIndex + 1} / {filteredPhotos.length}
                                </span>
                                <button
                                    className="text-gray-400 hover:text-white transition-colors"
                                    onClick={() => setSelectedIndex(null)}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <img
                            src={selectedPhoto.imageUrl}
                            alt={selectedPhoto.name || 'Community photo'}
                            className="w-full max-h-[82vh] object-contain rounded-xl"
                        />

                        <p className="text-center text-gray-600 text-xs mt-3">
                            Use ← → arrow keys to navigate · Esc to close
                        </p>
                    </div>
                </div>
            )}

            {/* ── Upload modal ─────────────────────────────────────────── */}
            {modalOpen && user && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
                    onClick={() => !uploading && resetModal()}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-7 space-y-5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between">
                            <div>
                                <h3
                                    className="font-bold text-xl leading-tight"
                                    style={{ fontFamily: "'Syne', sans-serif" }}
                                >
                                    Upload Photo
                                </h3>
                                <p className="text-gray-400 text-sm mt-0.5">
                                    Images are compressed automatically.
                                </p>
                            </div>
                            <button
                                onClick={() => !uploading && resetModal()}
                                disabled={uploading}
                                className="p-2 rounded-xl text-gray-400 hover:text-black hover:bg-gray-100 transition-all"
                                aria-label="Close"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* File picker */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Photo</label>
                            <label
                                className={`flex flex-col items-center justify-center w-full rounded-2xl border-2 border-dashed transition-colors overflow-hidden cursor-pointer ${
                                    uploading
                                        ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                                        : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                                }`}
                                style={{ minHeight: preview ? 'auto' : '9rem' }}
                            >
                                {preview ? (
                                    <img
                                        src={preview}
                                        alt="Preview"
                                        className="w-full max-h-52 object-cover rounded-2xl"
                                    />
                                ) : (
                                    <div className="flex flex-col items-center gap-1.5 py-8">
                                        <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                        </svg>
                                        <span className="text-sm font-medium text-gray-500">Click to select a photo</span>
                                        <span className="text-xs text-gray-400">Any format · JPG, PNG, HEIC, WEBP…</span>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    disabled={uploading}
                                    className="hidden"
                                />
                            </label>
                            {preview && !uploading && (
                                <button
                                    type="button"
                                    onClick={() => { setFile(null); setPreview(null); }}
                                    className="mt-1.5 text-xs text-gray-400 hover:text-gray-700 transition-colors"
                                >
                                    Change photo
                                </button>
                            )}
                        </div>

                        {/* Photo name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Name
                            </label>
                            <input
                                type="text"
                                value={photoName}
                                onChange={(e) => setPhotoName(e.target.value)}
                                disabled={uploading}
                                placeholder="e.g. Spring Shoe Drive 2025"
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:opacity-60"
                            />
                        </div>

                        {/* Tag */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Tag</label>

                            {!addingNewTag ? (
                                <div className="flex gap-2">
                                    <select
                                        value={chosenTag}
                                        onChange={(e) => setChosenTag(e.target.value)}
                                        disabled={uploading}
                                        className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:opacity-60"
                                    >
                                        {tags.length === 0 && (
                                            <option value="" disabled>No tags yet</option>
                                        )}
                                        {tags.map((t) => (
                                            <option key={t} value={t}>{toDisplay(t)}</option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={() => setAddingNewTag(true)}
                                        disabled={uploading}
                                        className="px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-800 transition-colors disabled:opacity-60 whitespace-nowrap"
                                    >
                                        + New tag
                                    </button>
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newTagValue}
                                        onChange={(e) => setNewTagValue(e.target.value)}
                                        disabled={uploading}
                                        placeholder="e.g. events"
                                        className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:opacity-60"
                                        autoFocus
                                    />
                                    {tags.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => { setAddingNewTag(false); setNewTagValue(''); }}
                                            disabled={uploading}
                                            className="px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-800 transition-colors disabled:opacity-60 whitespace-nowrap"
                                        >
                                            Use existing
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Status */}
                        {uploadMsg && (
                            <p className={`text-sm font-medium text-center ${
                                uploadMsg.includes('complete') ? 'text-green-600' : 'text-red-500'
                            }`}>
                                {uploadMsg}
                            </p>
                        )}

                        {/* Submit */}
                        <button
                            type="button"
                            onClick={handleUpload}
                            disabled={uploading || !file || !photoName.trim() || !(addingNewTag ? newTagValue.trim() : chosenTag)}
                            className="w-full py-3 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {uploading ? (
                                <>
                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Uploading…
                                </>
                            ) : (
                                'Upload'
                            )}
                        </button>
                    </div>
                </div>
            )}

            <Footer />
        </>
    );
};

export default PhotoLibrary;
